import classNames from 'classnames';
import htmlToDraftJS from 'html-to-draftjs';

import draftJSToHtml from 'draftjs-to-html';
import {
  AtomicBlockUtils,
  ContentState,
  EditorState,
  RawDraftContentState,
} from 'draft-js';
import EditIcon from '@mui/icons-material/Edit';
import React, { useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { makeStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useTranslation } from 'react-i18next';
import CodeIcon from '@mui/icons-material/Code';
import ModalAddEditTable from './_module/ModalAddEditTable';
import ModalEditCode from './_module/ModalEditCode';
import useNotification from '../../../../_hook/useNotification';
import './_module/react-draft-wysiwyg.css';
import { ITempUploadSchema } from '../../../../../../graphql/type/tempUpload';
import { useUploadTempFileMutation } from '../../../../../../graphql/mutation/tempUpload/uploadTempFileMutation';
import ModalEditImageLink from './_module/ModalEditImageLink';

export const getTableDataWithColSpanRowSpan = (tableData: any) => {
  const tableDataWithColSpanRowSpan = [];
  const rowLength = tableData.length;

  if (rowLength) {
    const colLength = tableData[0].length;

    // get colSpan and RowSpan
    for (let rowPosition = 0; rowPosition < rowLength; rowPosition += 1) {
      const rowData = [];

      for (let colPosition = 0; colPosition < colLength; colPosition += 1) {
        const currentDataText = tableData[rowPosition][colPosition];
        let colSpan = 1;
        let rowSpan = 1;

        for (
          let compareColPosition = colPosition + 1;
          compareColPosition < colLength;
          compareColPosition += 1
        ) {
          if (currentDataText === tableData[rowPosition][compareColPosition]) {
            if (rowPosition === 0) {
              colSpan += 1;
            } else {
              for (
                let targetRowPosition = rowPosition - 1;
                targetRowPosition >= 0;
                targetRowPosition -= 1
              ) {
                const targetColumnData =
                  tableDataWithColSpanRowSpan[targetRowPosition][
                    compareColPosition
                  ];

                if (
                  currentDataText === targetColumnData.text &&
                  targetColumnData.rowSpan !== 1
                ) {
                  break;
                } else {
                  colSpan += 1;
                }
              }
            }
          } else {
            break;
          }
        }

        for (
          let compareRowPosition = rowPosition + 1;
          compareRowPosition < rowLength;
          compareRowPosition += 1
        ) {
          if (currentDataText === tableData[compareRowPosition][colPosition]) {
            let matchColumnCount = 1;

            for (
              let targetCellPosition = 1;
              targetCellPosition < colSpan;
              targetCellPosition += 1
            ) {
              if (
                currentDataText ===
                tableData[compareRowPosition][colPosition + targetCellPosition]
              ) {
                matchColumnCount += 1;
              }
            }

            if (matchColumnCount === colSpan) {
              rowSpan += 1;
            }
          } else {
            break;
          }
        }
        rowData.push({
          text: currentDataText,
          colSpan,
          rowSpan,
        });
      }

      tableDataWithColSpanRowSpan.push(rowData);
    }
    const tableDataWithColSpanRowSpanOriginal = JSON.parse(
      JSON.stringify(tableDataWithColSpanRowSpan)
    ); // Clone array

    // Remove siblings if colSpan greater than 1
    for (let rowPosition = 0; rowPosition < rowLength; rowPosition += 1) {
      for (let colPosition = 0; colPosition < colLength; colPosition += 1) {
        const trData = tableDataWithColSpanRowSpan[rowPosition][colPosition];

        if (trData) {
          const { text, colSpan, rowSpan } = trData;

          for (
            let deleteColPosition = 1;
            deleteColPosition < colSpan;
            deleteColPosition += 1
          ) {
            if (
              rowPosition !== 0 &&
              tableDataWithColSpanRowSpanOriginal[rowPosition - 1][colPosition]
                .rowSpan > 1
            ) {
              const upsideData =
                tableDataWithColSpanRowSpanOriginal[rowPosition - 1][
                  colPosition + deleteColPosition
                ];
              const {
                text: upsideDataText,
                colSpan: upsideDataColSpan,
                rowSpan: upsideDataRowSpan,
              } = upsideData;

              if (text === upsideDataText) {
                delete tableDataWithColSpanRowSpan[rowPosition][
                  colPosition + deleteColPosition
                ];
              }
            } else {
              delete tableDataWithColSpanRowSpan[rowPosition][
                colPosition + deleteColPosition
              ];
            }
          }
        }
      }
    }

    // Remove siblings if RowSpan greater than 1
    for (let rowPosition = 0; rowPosition < rowLength; rowPosition += 1) {
      for (let colPosition = 0; colPosition < colLength; colPosition += 1) {
        const trData = tableDataWithColSpanRowSpan[rowPosition][colPosition];

        if (trData) {
          const { text, colSpan, rowSpan } = trData;

          for (
            let deleteRowPosition = 1;
            deleteRowPosition < rowSpan;
            deleteRowPosition += 1
          ) {
            delete tableDataWithColSpanRowSpan[rowPosition + deleteRowPosition][
              colPosition
            ];

            if (colSpan > 1) {
              for (
                let deleteColumnPosition = 1;
                deleteColumnPosition < colSpan;
                deleteColumnPosition += 1
              ) {
                delete tableDataWithColSpanRowSpan[
                  rowPosition + deleteRowPosition
                ][colPosition + deleteColumnPosition];
              }
            }
          }
        }
      }
    }
  }

  return tableDataWithColSpanRowSpan;
};

export const htmlToDraft = (html: string) => {
  // Add empty P tag prevent draft js broken
  if (!html) {
    html = '<p></p>';
  }

  if (!html.startsWith('<p></p>')) {
    html = `<p></p>${html}`;
  }

  if (!html.trim().endsWith('<p></p>')) {
    html += '<p></p>';
  }
  return htmlToDraftJS(html, (nodeName, node) => {
    if (nodeName === 'table') {
      const bodyData: any[] = [];
      const table = node;
      const thead = table.getElementsByTagName('thead')[0];
      const tbody = table.getElementsByTagName('tbody')[0];
      const tfoot = table.getElementsByTagName('tfoot')[0];
      const theadTrNodes = thead ? thead.getElementsByTagName('tr') : null;
      const tbodyTrNodes = tbody.getElementsByTagName('tr');
      const tfootTrNodes = tfoot ? tfoot.getElementsByTagName('tr') : null;
      const columnCount = tbodyTrNodes[0].getElementsByTagName('td').length;
      const headData: any[] = [];
      const footData: any[] = [];

      if (theadTrNodes) {
        // Init data structure
        for (let count = 0; count < theadTrNodes.length; count += 1) {
          const rowData = [];

          for (let count2 = 0; count2 < columnCount; count2 += 1) {
            rowData.push(null);
          }
          headData.push(rowData);
        }

        Array.prototype.forEach.call(theadTrNodes, (trNode, rowIndex) => {
          const tdNodes = trNode.getElementsByTagName('th');
          let colIndex = 0;

          Array.prototype.forEach.call(tdNodes, (tdNode) => {
            const colspan = tdNode.getAttributeNode('colspan')
              ? Number(tdNode.getAttributeNode('colspan').value)
              : 1;
            const rowspan = tdNode.getAttributeNode('rowspan')
              ? Number(tdNode.getAttributeNode('rowspan').value)
              : 1;
            const value = tdNode.textContent;

            for (let colPosition = 0; colPosition < colspan; colPosition += 1) {
              if (headData[rowIndex][colPosition + colIndex] === null) {
                headData[rowIndex][colPosition + colIndex] = value;
              } else {
                if (colIndex < columnCount) {
                  colIndex += 1;
                  colPosition -= 1;
                }
              }
            }
            for (let rowPosition = 0; rowPosition < rowspan; rowPosition += 1) {
              headData[rowIndex + rowPosition][colIndex] = value;

              if (colspan > 1) {
                for (
                  let targetColumnPosition = 1;
                  targetColumnPosition < colspan;
                  targetColumnPosition += 1
                ) {
                  headData[rowIndex + rowPosition][
                    colIndex + targetColumnPosition
                  ] = value;
                }
              }
            }
            colIndex += colspan;
          });
        });
      }
      // Tbody
      Array.prototype.forEach.call(tbodyTrNodes, (trNode) => {
        const trData: any[] = [];
        const tdNodes = trNode.getElementsByTagName('td');

        Array.prototype.forEach.call(tdNodes, (tdNode) => {
          trData.push(tdNode.textContent);
        });
        bodyData.push(trData);
      });
      // Tfoot
      if (tfootTrNodes) {
        Array.prototype.forEach.call(tfootTrNodes, (trNode) => {
          const trData: any[] = [];
          const tdNodes = trNode.getElementsByTagName('td');

          Array.prototype.forEach.call(tdNodes, (tdNode) => {
            trData.push(tdNode.textContent);
          });
          footData.push(trData);
        });
      }

      return {
        type: 'TABLE',
        mutability: 'MUTABLE',
        data: {
          tableData: {
            header: headData,
            body: bodyData,
            footer: footData,
          },
        },
      };
    }

    if (
      nodeName === 'a' &&
      ((node.firstChild &&
        node.firstChild.nextSibling &&
        node.firstChild.nextSibling.nodeName.toLowerCase() === 'img') ||
        (node.firstChild &&
          (node.firstChild as HTMLElement).classList &&
          (node.firstChild as HTMLElement).classList.contains(
            'draft-editor-image-wrapper'
          ) &&
          node.firstChild.nodeName.toLowerCase() === 'div'))
    ) {
      const hasDiv = node.firstChild.nodeName.toLowerCase() === 'div';
      let imgNode: any;
      let divNode: any;

      if (hasDiv && node.firstChild && node.firstChild.firstChild) {
        divNode = node.firstChild;
        imgNode = node.firstChild.firstChild;
      } else {
        imgNode = node.firstChild.nextSibling;
      }
      const entityConfig: any = {};

      entityConfig.src = imgNode.getAttribute
        ? imgNode.getAttribute('src') || imgNode.src
        : imgNode.src;
      entityConfig.alt = imgNode.alt;
      entityConfig.height = imgNode.style.height;
      entityConfig.width = imgNode.style.width;
      entityConfig.destination = node.getAttribute
        ? node.getAttribute('href') || ''
        : '';
      if (imgNode.style.float) {
        entityConfig.alignment = imgNode.style.float;
      }

      if (divNode && divNode.style.textAlign) {
        entityConfig.alignment =
          divNode.style.textAlign === 'center'
            ? 'none'
            : divNode.style.textAlign;
      }

      return {
        type: 'IMAGE',
        mutability: 'MUTABLE',
        data: entityConfig,
      };
    }

    if (
      (nodeName === 'a' &&
        node.firstChild &&
        node.firstChild.nodeName.toLowerCase() === 'img') ||
      (nodeName === 'div' &&
        node.classList &&
        node.classList.contains('draft-editor-image-wrapper') &&
        node.firstChild &&
        node.firstChild.nodeName.toLowerCase() === 'img')
    ) {
      const entityConfig: any = {};
      const imgNode: any = node.firstChild;

      entityConfig.src = imgNode.getAttribute
        ? imgNode.getAttribute('src') || imgNode.src
        : imgNode.src;
      entityConfig.alt = imgNode.alt;
      entityConfig.height = imgNode.style.height;
      entityConfig.width = imgNode.style.width;
      entityConfig.destination = node.getAttribute
        ? node.getAttribute('href') || ''
        : '';
      if (imgNode.style.float) {
        entityConfig.alignment = imgNode.style.float;
      }

      if (node && node.style.textAlign) {
        entityConfig.alignment =
          node.style.textAlign === 'center' ? 'none' : node.style.textAlign;
      }

      return {
        type: 'IMAGE',
        mutability: 'MUTABLE',
        data: entityConfig,
      };
    }
    return undefined;
  });
};

export const draftToHtml = (
  rawContentState: RawDraftContentState,
  hashtagConfig = {},
  directional = false
) =>
  draftJSToHtml(rawContentState, hashtagConfig, directional, (entity) => {
    if (entity.type === 'IMAGE') {
      let img = `<img src="${entity.data.src}" alt="${entity.data.alt}" style="height: ${entity.data.height};width: ${entity.data.width}"/>`;

      img = `<div class="draft-editor-image-wrapper" style="text-align: ${
        entity.data.alignment === 'none' || !entity.data.alignment
          ? 'center'
          : entity.data.alignment
      };font-size: 0;">${img}</div>`;
      if (entity.data.destination) {
        const { destination } = entity.data;
        const finalDestination =
          destination.startsWith('http://') ||
          destination.startsWith('https://')
            ? destination
            : `//${destination}`;
        return `<a href="${finalDestination}" rel="noopener noreferrer" target="_blank">${img}</a>`;
      }

      return img;
    }

    if (entity.type === 'EMBEDDED_LINK') {
      let { src } = entity.data;

      if (src.indexOf('youtube') >= 0) {
        src = src.replace('watch?v=', 'embed/');
        src = src.replace('/watch/', '/embed/');
        src = src.replace('youtu.be/', 'youtube.com/embed/');
      }
      return '<iframe width="'
        .concat(entity.data.width, '" height="')
        .concat(entity.data.height, '" src="')
        .concat(src, '" frameBorder="0"></iframe>');
    }

    if (entity.type !== 'TABLE') {
      return undefined;
    }
    const { tableData } = entity.data;
    let table = '<table>';

    // Header
    table += '<thead>';

    // tableData.header.forEach(
    // 	(trData) => {
    // 		table += '<tr>'
    // 		trData.forEach((data) => {
    // 			table += `<th>${ data }</th>`
    // 		})
    // 		table += '</tr>'
    // 	}
    // )
    const tableHeader = getTableDataWithColSpanRowSpan(tableData.header);

    tableHeader.forEach((trData) => {
      table += '<tr>';
      trData.forEach((tdData) => {
        const { text, colSpan, rowSpan } = tdData;

        table += `<th colspan="${colSpan}" rowspan="${rowSpan}">${text}</th>`;
      });
      table += '</tr>';
    });
    table += '</thead>';

    // Body
    table += '<tbody>';
    tableData.body.forEach((trData: any) => {
      table += '<tr>';
      trData.forEach((data: any) => {
        table += `<td>${data}</td>`;
      });
      table += '</tr>';
    });
    table += '</tbody>';

    // footer
    table += '<tfoot>';

    tableData.footer.forEach((trData: any) => {
      table += '<tr>';
      trData.forEach((tdData: any) => {
        table += '<td>';
        table += tdData;
        table += '</td>';
      });

      table += '</tr>';
    });

    table += '</tfoot>';

    table += '</table>';
    return table;
  });

function Option(props: any) {
  const {
    disabled,
    onClick,
    value,
    children,
    className,
    activeClassName,
    active,
    title,
  } = props;

  return (
    <div
      className={classNames('rdw-option-wrapper', className, {
        'rdw-option-active': active,
        [activeClassName || '']: active,
        'rdw-option-disabled': disabled,
      })}
      onClick={!disabled ? () => onClick(value) : undefined}
      aria-selected={active}
      title={title}
    >
      {children}
    </div>
  );
}

const Embed = ({ block, contentState }: any) => {
  const entity = contentState.getEntity(block.getEntityAt(0));
  let {
    // eslint-disable-next-line prefer-const
    src,
    // eslint-disable-next-line prefer-const
    height,
    // eslint-disable-next-line prefer-const
    width,
  }: any = entity.getData();

  if (src.indexOf('youtube') >= 0) {
    src = src.replace('watch?v=', 'embed/');
    src = src.replace('/watch/', '/embed/');
    src = src.replace('youtu.be/', 'youtube.com/embed/');
  }

  return (
    <iframe
      height={height}
      width={width}
      src={src}
      frameBorder="0"
      allowFullScreen
      title="Wysiwyg Embedded Content"
    />
  );
};

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
    width: '100%',
    maxWidth: '100%',
    border: '1px solid #dddddd',
    borderCollapse: 'collapse',
    textAlign: 'left',
    '& td,th': {
      padding: '7px 10px',
      borderLeft: '1px solid #ebebeb',
    },
    '& tr': {
      borderTop: '1px solid #ebebeb',
      borderBottom: '1px solid #ebebeb',
    },
    '& thead': {
      borderBottom: '5px solid #ebebeb',
    },
    '& tfoot': {
      borderTop: '5px solid #ebebeb',
    },
    '& > tbody > tr:nth-of-type(odd)': {
      backgroundColor: theme.palette.mode === 'light' ? '#f9f9f9' : '#474747',
    },
  },
  imageOptionPopup: {
    position: 'absolute',
    background: theme.palette.mode === 'light' ? 'white' : 'black',
    display: 'flex',
    padding: '5px 2px',
    borderRadius: '2px',
    border: '1px solid #f1f1f1',
    width: '105px',
    cursor: 'pointer',
    zIndex: '100',
  },
  editor: {
    padding: '5px',
    backgroundColor:
      theme.palette.mode === 'light'
        ? '#f3f3f3 !important'
        : '#4a4a4a !important',
  },
  toolbar: {
    padding: '0 !important',
    margin: '0 !important',
    backgroundColor:
      theme.palette.mode === 'light'
        ? '#e6e6e6 !important'
        : '#1e1e1e !important',
    borderColor:
      theme.palette.mode === 'light'
        ? '#f1f1f1 !important'
        : '#525252 !important',
    '& .rdw-option-wrapper': {
      color: '#000 !important',
      background:
        theme.palette.mode === 'light'
          ? '#fff !important'
          : '#a7a7a7 !important',
    },
    '& .rdw-dropdown-wrapper': {
      '& a': {
        color:
          theme.palette.mode === 'light'
            ? '#000 !important'
            : '#000 !important',
      },
    },
    '& .rdw-dropdownoption-default': {
      color:
        theme.palette.mode === 'light' ? '#000 !important' : '#000 !important',
    },
  },
}));
const getImageComponent = (config: any, handleModalImageLinkShow: any) =>
  function Image(props: any) {
    const [hovered, setHovered] = useState<boolean>(false);
    const { block, contentState } = props;
    const classes = useStyles();
    const { isReadOnly, isImageAlignmentEnabled } = config;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const {
      src,
      alignment,
      height,
      width,
      alt,
      destination,
    } = entity.getData();

    function setEntityAlignment(tempAlignment: string) {
      const entityKey = block.getEntityAt(0);

      contentState.mergeEntityData(entityKey, { alignment: tempAlignment });
      config.onChange(
        EditorState.push(
          config.getEditorState(),
          contentState,
          'change-block-data'
        )
      );
    }

    function setEntityAlignmentLeft() {
      setEntityAlignment('left');
    }

    function setEntityAlignmentRight() {
      setEntityAlignment('right');
    }

    function setEntityAlignmentCenter() {
      setEntityAlignment('none');
    }

    function toggleHovered() {
      setHovered(!hovered);
    }

    function renderAlignmentOptions(tempAlignment: string) {
      return (
        <div
          style={{ width: '100%' }}
          className={classNames(classes.imageOptionPopup, {
            'rdw-image-alignment-options-popup-right':
              tempAlignment === 'right',
          })}
        >
          <Option
            onClick={setEntityAlignmentLeft}
            className="rdw-image-alignment-option"
          >
            L
          </Option>
          <Option
            onClick={setEntityAlignmentCenter}
            className="rdw-image-alignment-option"
          >
            C
          </Option>
          <Option
            onClick={setEntityAlignmentRight}
            className="rdw-image-alignment-option"
          >
            R
          </Option>
          <Option
            className="rdw-image-alignment-option-link"
            onClick={() => {
              handleModalImageLinkShow(destination, block.getEntityAt(0));
            }}
          >
            {destination || 'No Link'}
          </Option>
        </div>
      );
    }

    return (
      <span
        onMouseEnter={toggleHovered}
        onMouseLeave={toggleHovered}
        className={classNames('rdw-image-alignment', {
          'rdw-image-left': alignment === 'left',
          'rdw-image-right': alignment === 'right',
          'rdw-image-center': !alignment || alignment === 'none',
        })}
      >
        <span className="rdw-image-imagewrapper">
          <img
            src={src}
            alt={alt}
            style={{
              height,
              width,
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
          {!isReadOnly() && hovered && isImageAlignmentEnabled()
            ? renderAlignmentOptions(alignment)
            : undefined}
        </span>
      </span>
    );
  };

const getTableComponent = (
  config: any,
  handleModalTableShow: any,
  removeTable: any
) =>
  function Table(props: any) {
    const [hovered, setHovered] = useState<boolean>(false);
    const { block, contentState } = props;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const { isReadOnly } = config;
    const classes = useStyles();
    const { tableData } = entity.getData();
    const tableHeader = getTableDataWithColSpanRowSpan(tableData.header);

    return (
      <div
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
      >
        <table className={classes.table}>
          <thead>
            {tableHeader.map((trData, trIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={trIndex}>
                {trData.map((thData, tdIndex) => {
                  const { text, colSpan, rowSpan } = thData;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <th key={tdIndex} colSpan={colSpan} rowSpan={rowSpan}>
                      {text}
                    </th>
                  );
                })}
              </tr>
            ))}
            {/* {tableData.header.map( */}
            {/*	(trData, index) => <tr key={index}> */}
            {/*		{trData.map( */}
            {/*			(tdData, index2) => <th key={index2}> */}
            {/*				{tdData} */}
            {/*			</th> */}
            {/*		)} */}
            {/*	</tr> */}
            {/* )} */}
          </thead>
          <tbody>
            {tableData.body.map((trData: any, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                {trData.map((tdData: any, index2: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={index2}>{tdData}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {tableData.footer.map((trData: any, trIndex: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={trIndex}>
                {trData.map((tdData: any, tdIndex: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={tdIndex}>{tdData}</td>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
        {!isReadOnly() && hovered && (
          <>
            <Button
              color="primary"
              variant="contained"
              startIcon={<EditIcon />}
              onMouseDown={(e) => {
                e.preventDefault();
                handleModalTableShow(tableData, block.getEntityAt(0));
              }}
            >
              Edit
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<DeleteIcon />}
              onMouseDown={(e) => {
                e.preventDefault();
                removeTable(block.getEntityAt(0), block, contentState);
              }}
            >
              Remove
            </Button>
          </>
        )}
      </div>
    );
  };

interface IProps {
  editorState: EditorState;
  onUploadedImage?: (fileObj: ITempUploadSchema[]) => void;
  disableToolbarTable?: boolean;
  disableToolbarCodeView?: boolean;
  onEditorStateChange: (state: EditorState) => void;
  enableUploadImage?: boolean;
}

export default function Wysiwyg(props: IProps) {
  const { notification } = useNotification();
  const { t } = useTranslation();
  const classes = useStyles();
  const [modalTableVisible, setModalTableVisible] = useState<boolean>(false);
  const [editTableData, setEditTableData] = useState<any>(null);
  const [editEntityKey, setEditEntityKey] = useState<any>(null);
  const [modalCodeVisible, setModalCodeVisible] = useState<boolean>(false);

  const [editImageLinkDestination, setEditImageLinkDestination] = useState<any>(
    ''
  );
  const [editImageLinkEntityKey, setEditImageLinkEntityKey] = useState<any>(
    null
  );
  const [modalImageLinkVisible, setModalImageLinkVisible] = useState<boolean>(
    false
  );

  const {
    editorState,
    onUploadedImage,
    disableToolbarTable,
    disableToolbarCodeView,
    onEditorStateChange,
    enableUploadImage,
  } = props;
  const [uploadTempFileMutation] = useUploadTempFileMutation(
    `
                tempUploads {
                   _id
                   filename
                   originalFilename
                   extension
                   mimeType
                   sizeInKb
                   filenameUrl
                }
              `,
    {
      onCompleted: (data) => {
        if (
          onUploadedImage &&
          data &&
          data.uploadTempFile &&
          data.uploadTempFile.tempUploads
        ) {
          onUploadedImage(data.uploadTempFile.tempUploads);
        }
      },
    }
  );

  function forceEditorUpdate() {
    const selection = editorState.getSelection();

    onEditorStateChange(EditorState.forceSelection(editorState, selection));
  }

  function handleModalTableClose() {
    setModalTableVisible(false);
  }

  function handleModalTableShow(
    newEditTableData?: any,
    newEditEntityKey?: any
  ) {
    setModalTableVisible(true);
    setEditTableData(newEditTableData || null);
    setEditEntityKey(newEditEntityKey || null);
  }

  function handleModalImageLinkShow(
    destination?: any,
    tempEditEntityKey?: any
  ) {
    setModalImageLinkVisible(true);
    setEditImageLinkDestination(destination || null);
    setEditImageLinkEntityKey(tempEditEntityKey || null);
  }

  function saveImageLink(newEditImageLinkDestination: string) {
    editorState.getCurrentContent().mergeEntityData(editImageLinkEntityKey, {
      destination: newEditImageLinkDestination || '',
    });
    onEditorStateChange(
      EditorState.push(
        editorState,
        editorState.getCurrentContent(),
        'change-block-data'
      )
    );
    forceEditorUpdate();

    setModalImageLinkVisible(false);
  }

  function addEditTable(tableData: any) {
    if (editTableData) {
      editorState
        .getCurrentContent()
        .mergeEntityData(editEntityKey, { tableData });
      onEditorStateChange(
        EditorState.push(
          editorState,
          editorState.getCurrentContent(),
          'change-block-data'
        )
      );
      forceEditorUpdate();
    } else {
      const entityKey = editorState
        .getCurrentContent()
        .createEntity('TABLE', 'MUTABLE', { tableData })
        .getLastCreatedEntityKey();
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(
        editorState,
        entityKey,
        ' '
      );

      onEditorStateChange(newEditorState);
    }

    handleModalTableClose();
  }

  function removeTable(
    entityKey: any,
    atomicBlock: any,
    contentState: ContentState
  ) {
    let blockArray = contentState.getBlocksAsArray();

    blockArray = blockArray.filter((block) => {
      if (block.getKey() === atomicBlock.getKey()) {
        return false;
      }
      return true;
    });

    const newContentState = ContentState.createFromBlockArray(blockArray);

    onEditorStateChange(EditorState.createWithContent(newContentState));
  }

  function handleModalCodeClose() {
    setModalCodeVisible(false);
  }

  function handleModalCodeShow() {
    setModalCodeVisible(true);
  }

  function editCode(rawHTML: string) {
    const contentBlock = htmlToDraft(rawHTML);
    const newEditorState = EditorState.createWithContent(
      ContentState.createFromBlockArray(contentBlock.contentBlocks)
    );

    onEditorStateChange(newEditorState);

    handleModalCodeClose();
  }

  function customBlockRenderFunc(block: any, config: any) {
    if (block.getType() === 'atomic') {
      const contentState = config.getEditorState().getCurrentContent();
      const entity = contentState.getEntity(block.getEntityAt(0));

      if (entity && entity.type === 'IMAGE') {
        return {
          component: getImageComponent(
            config,
            (destination: any, editBlock: any) => {
              handleModalImageLinkShow(destination, editBlock);
            }
          ),
          editable: false,
        };
      }
      if (entity && entity.type === 'EMBEDDED_LINK') {
        return {
          component: Embed,
          editable: false,
        };
      }
      if (entity && entity.type === 'TABLE') {
        return {
          component: getTableComponent(
            config,
            (tempEditTableData: any, editBlock: any) => {
              handleModalTableShow(tempEditTableData, editBlock);
            },
            removeTable
          ),
          editable: false,
        };
      }
    }

    return undefined;
  }

  const toolbarCustomButtons = [];

  if (!disableToolbarTable) {
    toolbarCustomButtons.push(
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        key="table"
        className="rdw-remove-wrapper"
        onClick={() => {
          handleModalTableShow();
        }}
      >
        <div className="rdw-option-wrapper" title="Table">
          <TableChartIcon />
        </div>
      </div>
    );
  }

  if (!disableToolbarCodeView) {
    toolbarCustomButtons.push(
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        key="code"
        className="rdw-remove-wrapper"
        onClick={() => {
          handleModalCodeShow();
        }}
      >
        <div className="rdw-option-wrapper" title="Code">
          <CodeIcon />
        </div>
      </div>
    );
  }

  return (
    <>
      <ModalAddEditTable
        key={`${modalTableVisible}ModalAddEditTable`}
        tableData={editTableData}
        visible={modalTableVisible}
        onCancel={() => {
          handleModalTableClose();
          forceEditorUpdate();
        }}
        maxWidth="lg"
        onSubmit={addEditTable}
      />
      <ModalEditImageLink
        key={`${modalImageLinkVisible}ModalEditImageLink`}
        visible={modalImageLinkVisible}
        editImageLinkDestination={editImageLinkDestination}
        onCancel={() => {
          setModalImageLinkVisible(false);
          forceEditorUpdate();
        }}
        maxWidth="sm"
        onSubmit={saveImageLink}
      />
      <ModalEditCode
        key={`${modalCodeVisible}ModalEditCode`}
        visible={modalCodeVisible}
        editorState={editorState}
        onCancel={() => {
          handleModalCodeClose();
          forceEditorUpdate();
        }}
        maxWidth="sm"
        onSubmit={editCode}
      />
      <Editor
        editorClassName={classes.editor}
        toolbarClassName={classes.toolbar}
        editorState={editorState}
        toolbar={{
          ...{
            options: [
              'inline',
              'fontSize',
              'colorPicker',
              'list',
              'textAlign',
              'image',
              'embedded',
              'link',
              'history',
              'remove',
            ],
            inline: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: [
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'monospace',
                'superscript',
                'subscript',
              ],
            },
            blockType: {
              inDropdown: true,
              options: [
                'Normal',
                'H1',
                'H2',
                'H3',
                'H4',
                'H5',
                'H6',
                'Blockquote',
                'Code',
              ],
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
            },
            fontSize: {
              options: [
                8,
                9,
                10,
                11,
                12,
                14,
                16,
                18,
                24,
                30,
                36,
                48,
                60,
                72,
                96,
              ],
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
            },
            textAlign: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: ['left', 'center', 'right', 'justify'],
            },
            colorPicker: {
              className: undefined,
              component: undefined,
              popupClassName: undefined,
              colors: [
                'rgb(97,189,109)',
                'rgb(26,188,156)',
                'rgb(84,172,210)',
                'rgb(44,130,201)',
                'rgb(147,101,184)',
                'rgb(71,85,119)',
                'rgb(204,204,204)',
                'rgb(65,168,95)',
                'rgb(0,168,133)',
                'rgb(61,142,185)',
                'rgb(41,105,176)',
                'rgb(85,57,130)',
                'rgb(40,50,78)',
                'rgb(0,0,0)',
                'rgb(247,218,100)',
                'rgb(251,160,38)',
                'rgb(235,107,86)',
                'rgb(226,80,65)',
                'rgb(163,143,132)',
                'rgb(239,239,239)',
                'rgb(255,255,255)',
                'rgb(250,197,28)',
                'rgb(243,121,52)',
                'rgb(209,72,65)',
                'rgb(184,49,47)',
                'rgb(124,112,107)',
                'rgb(255,0,0)',
                'rgb(255,255,0)',
                'rgb(255,0,255)',
                'rgb(0,255,0)',
                'rgb(0,255,255)',
                'rgb(0,0,255)',
                'rgb(209,213,216)',
              ],
            },
            list: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: ['unordered', 'ordered', 'indent', 'outdent'],
            },
            link: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              popupClassName: undefined,
              dropdownClassName: undefined,
              showOpenOptionOnHover: true,
              defaultTargetOption: '_self',
              options: ['link', 'unlink'],
              linkCallback: undefined,
            },
            // image : {
            // 	className : undefined,
            // 	component : undefined,
            // 	popupClassName : undefined,
            // 	urlEnabled : true,
            // 	uploadEnabled : true,
            // 	alignmentEnabled : true,
            // 	uploadCallback : undefined,
            // 	previewImage : false,
            // 	inputAccept : 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
            // 	alt : {
            // 		present : false, mandatory : false
            // 	},
            // 	defaultSize : {
            // 		height : 'auto',
            // 		width : 'auto'
            // 	}
            // },
            history: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: ['undo', 'redo'],
            },
          },
          ...(enableUploadImage && onUploadedImage
            ? {
                image: {
                  urlEnabled: true,
                  uploadEnabled: true,
                  alignmentEnabled: true,
                  uploadCallback: (file: File) =>
                    new Promise((resolve, reject) => {
                      if (file && file.size > 512000) {
                        notification.error(
                          t('__fileName__is_over__size__limit', {
                            fileName: file.name,
                            size: '500KB',
                          })
                        );

                        reject();
                      } else {
                        uploadTempFileMutation({
                          variables: {
                            files: [file],
                          },
                        }).then(({ data }) => {
                          if (
                            data &&
                            data.uploadTempFile &&
                            data.uploadTempFile.tempUploads &&
                            data.uploadTempFile.tempUploads.length
                          ) {
                            resolve({
                              data: {
                                link:
                                  data.uploadTempFile.tempUploads[0]
                                    .filenameUrl,
                              },
                            });
                          }
                        });
                      }
                    }),
                  previewImage: true,
                  inputAccept:
                    'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                  alt: {
                    present: false,
                    mandatory: false,
                  },
                  defaultSize: {
                    height: 'auto',
                    width: 'auto',
                  },
                },
              }
            : {}),
        }}
        onEditorStateChange={onEditorStateChange}
        toolbarCustomButtons={toolbarCustomButtons}
        customBlockRenderFunc={customBlockRenderFunc as any}
      />
    </>
  );
}
